/**
 * CategoryLayoutEditor — Elementor-style layout picker used inside the
 * Visual Builder RIGHT settings pane when a category / product-row /
 * product-grid section is selected.
 *
 * Writes the selected layout into the section's own component blob
 * (components[<compId>].layout, .desktop_cols, .tablet_cols, etc.). The
 * consumer reads these keys and switches its rendering strategy.
 *
 * All settings are safe — none affect price, stock, or checkout.
 */
import { useMemo } from "react";
import * as LucideIcons from "lucide-react";
import { X, Settings2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CATEGORY_LAYOUTS, LAYOUT_DEFAULTS, CATEGORY_LAYOUT_FIELDS } from "@/lib/visualBuilder/categoryLayouts";

export default function CategoryLayoutEditor({ compLabel, compId, values, onChange, onClose }) {
  const v = useMemo(() => ({ ...LAYOUT_DEFAULTS, ...(values || {}) }), [values]);

  const setField = (key, val) => onChange({ [key]: val });

  return (
    <div className="p-5 space-y-5" data-testid={`vb-cat-editor-${compId}`}>
      <div className="flex items-start gap-2">
        <div className="w-9 h-9 rounded-xl bg-slate-900 text-white grid place-items-center shrink-0">
          <Settings2 className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Category · Layout</div>
          <div className="text-[15px] font-semibold text-slate-900 truncate">{compLabel || compId}</div>
          <div className="text-[10.5px] font-mono text-slate-400 truncate">{compId}</div>
        </div>
        <button type="button" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100" data-testid="vb-cat-close">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Layout picker */}
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Layout Mode</Label>
        <div className="grid grid-cols-3 gap-2 mt-2">
          {CATEGORY_LAYOUTS.map((L) => {
            const Icon = LucideIcons[L.icon] || Settings2;
            const active = v.layout === L.value;
            return (
              <button
                key={L.value}
                type="button"
                onClick={() => setField("layout", L.value)}
                className={`group flex flex-col items-center gap-1 h-20 rounded-xl border p-2 transition-all ${
                  active ? "border-[#E32636] bg-[#FFF3F4] shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"
                }`}
                title={L.hint}
                data-testid={`vb-cat-layout-${L.value}`}
              >
                <Icon className={`w-4 h-4 ${active ? "text-[#E32636]" : "text-slate-500"}`} />
                <span className={`text-[11px] font-semibold ${active ? "text-[#E32636]" : "text-slate-700"}`}>{L.label}</span>
              </button>
            );
          })}
        </div>
        <div className="text-[10.5px] text-slate-400 mt-1.5">
          {CATEGORY_LAYOUTS.find((l) => l.value === v.layout)?.hint}
        </div>
      </div>

      {/* Layout fields (responsive cols, gap, autoplay etc.) */}
      <div className="grid grid-cols-2 gap-3">
        {CATEGORY_LAYOUT_FIELDS.map((f) => {
          if (f.showIfLayout && !f.showIfLayout.includes(v.layout)) return null;
          return (
            <div key={f.key} className={f.type === "toggle" ? "col-span-2" : "col-span-1"}>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">{f.label}</Label>
              {f.type === "number" && (
                <Input
                  type="number"
                  value={v[f.key] ?? ""}
                  onChange={(e) => setField(f.key, e.target.value === "" ? undefined : Number(e.target.value))}
                  min={f.min} max={f.max} step={f.step}
                  placeholder={String(LAYOUT_DEFAULTS[f.key] ?? "")}
                  className="h-8 text-[12px]"
                  data-testid={`vb-cat-field-${f.key}`}
                />
              )}
              {f.type === "toggle" && (
                <div className="flex items-center gap-2 h-8">
                  <Switch
                    checked={v[f.key] !== false && !!v[f.key]}
                    onCheckedChange={(val) => setField(f.key, val)}
                    data-testid={`vb-cat-field-${f.key}`}
                  />
                  <span className="text-[11px] text-slate-500">{v[f.key] ? "On" : "Off"}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="pt-3 text-[10.5px] text-slate-400 border-t border-slate-100 leading-relaxed">
        Layout affects visual arrangement only — product data (price, stock, discount) is untouched.
      </div>
    </div>
  );
}
